// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-20): the real-time streams' dates, kept on this
 * device, so Tools → Layers → Real time opens already filtered instead
 * of fetching every stream's dates on each visit.
 *
 * A stream's dates are kept with the link they were read from: a new
 * stream (no entry) or one whose link changed is fetched again, and a
 * retired one is dropped the next time the dates are saved. Live and
 * forecast streams gain frames every hour, so their dates are also
 * refreshed quietly once a few hours old; the old ones are shown until
 * the new ones arrive, so the list never empties while that runs.
 *
 * Public catalogue facts only: nothing about the viewer is kept.
 */

import type { Dataset } from '../types'

/** A span of dates, ms since epoch, end exclusive (`mapLayersUI.TimeRange`). */
export interface CachedRange {
  readonly startMs: number
  readonly endMs: number
}

export const RT_RANGE_STORAGE_KEY = 'ayni-rt-ranges.v1'

/** Live and forecast dates move every hour; this long, and they are fetched again. */
export const FAST_REFRESH_MS = 3 * 3_600_000
/** Everything else (monthly, yearly, projections) is checked once a week. */
export const SLOW_REFRESH_MS = 7 * 86_400_000

interface StoredEntry {
  /** The link the dates were read from. */
  readonly src: string
  readonly start: number
  readonly end: number
  /** When they were read, ms since epoch. */
  readonly at: number
}

interface Stored {
  readonly v: 1
  readonly entries: Record<string, StoredEntry>
}

/** Where a dataset's dates come from; '' when it has none to give. */
export function rangeSource(d: Pick<Dataset, 'releaseDescriptorLink' | 'timelineLink'>): string {
  return d.releaseDescriptorLink ?? d.timelineLink ?? ''
}

/** How long a dataset's kept dates stay fresh. */
export function refreshAfterMs(d: Pick<Dataset, 'realtimeKind'>): number {
  return d.realtimeKind === 'real-time' || d.realtimeKind === 'forecast' ? FAST_REFRESH_MS : SLOW_REFRESH_MS
}

function read(storage: Storage | null): Record<string, StoredEntry> {
  try {
    const raw = storage?.getItem(RT_RANGE_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<Stored>
    return parsed?.v === 1 && parsed.entries && typeof parsed.entries === 'object' ? parsed.entries : {}
  } catch {
    return {}
  }
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export interface LoadedRanges {
  /** The kept dates of the datasets given, by id, whose link has not changed. */
  readonly ranges: Map<string, CachedRange>
  /** The ids among them old enough to be fetched again, quietly. */
  readonly stale: Set<string>
}

/** The kept dates for these datasets. */
export function loadRtRanges(
  datasets: readonly Dataset[],
  now = Date.now(),
  storage: Storage | null = defaultStorage(),
): LoadedRanges {
  const entries = read(storage)
  const ranges = new Map<string, CachedRange>()
  const stale = new Set<string>()
  for (const d of datasets) {
    const e = entries[d.id]
    const src = rangeSource(d)
    if (!e || !src || e.src !== src) continue
    if (!(Number.isFinite(e.start) && Number.isFinite(e.end) && e.end > e.start)) continue
    ranges.set(d.id, { startMs: e.start, endMs: e.end })
    if (!(now - e.at < refreshAfterMs(d))) stale.add(d.id)
  }
  return { ranges, stale }
}

/**
 * Keep these dates. `fetchedAt` says when each was read (ids missing
 * from it keep the time they were kept with). Only datasets still in
 * `datasets` are kept, so a retired stream goes; a stream that gave no
 * dates is not kept, so it is asked again on the next visit.
 */
export function saveRtRanges(
  datasets: readonly Dataset[],
  ranges: ReadonlyMap<string, CachedRange | null>,
  fetchedAt: ReadonlyMap<string, number>,
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return
  const previous = read(storage)
  const entries: Record<string, StoredEntry> = {}
  for (const d of datasets) {
    const r = ranges.get(d.id)
    const src = rangeSource(d)
    if (!r || !src) continue
    const at = fetchedAt.get(d.id) ?? (previous[d.id]?.src === src ? previous[d.id].at : undefined)
    if (at === undefined) continue
    entries[d.id] = { src, start: r.startMs, end: r.endMs, at }
  }
  try {
    storage.setItem(RT_RANGE_STORAGE_KEY, JSON.stringify({ v: 1, entries } satisfies Stored))
  } catch {
    // A private window, or storage full: the dates last this visit.
  }
}
